import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { SpreadType } from '../../types';
import { generateVirtualSpread } from '../../services/virtualSpreadService';

interface VirtualSpreadProps {
  spreadType: SpreadType;
  onSpreadGenerated?: (cards: any[]) => void;
}

const spreadLayouts = {
  '1-card': 'grid-cols-1',
  '3-card': 'grid-cols-3',
  '5-card': 'grid-cols-3 grid-rows-3',
  'celtic-cross': 'grid-cols-4 grid-rows-4',
  'virtual-daily-card': 'grid-cols-1'
};

const VirtualSpread: React.FC<VirtualSpreadProps> = ({ spreadType, onSpreadGenerated }) => {
  const [cards, setCards] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<any | null>(null);

  const generateSpread = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const spreadToGenerate = spreadType === 'virtual-daily-card' ? '1-card' as SpreadType : spreadType;
      const newSpread = await generateVirtualSpread(spreadToGenerate);
      
      // Update position name for daily card
      if (spreadType === 'virtual-daily-card' && newSpread.length > 0) {
        newSpread[0].position = 'Daily Card';
      }
      
      setCards(newSpread);
      if (onSpreadGenerated) {
        onSpreadGenerated(newSpread);
      }
    } catch (error) {
      console.error('Error generating spread:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate spread');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateSpread();
  }, [spreadType]);

  const getCardPosition = (index: number) => {
    switch (spreadType) {
      case '1-card':
      case 'virtual-daily-card':
        return 'col-span-1';
      case '3-card':
        return 'col-span-1';
      case '5-card':
        return index === 0 
          ? 'col-start-2 row-start-2' 
          : index === 1 
          ? 'col-start-2 row-start-1'
          : index === 2 
          ? 'col-start-2 row-start-3'
          : index === 3 
          ? 'col-start-1 row-start-2'
          : 'col-start-3 row-start-2';
      case 'celtic-cross':
        return index === 0 
          ? 'col-start-2 row-start-2'
          : index === 1 
          ? 'col-start-2 row-start-2 rotate-90'
          : index === 2 
          ? 'col-start-2 row-start-3'
          : index === 3 
          ? 'col-start-1 row-start-2'
          : index === 4 
          ? 'col-start-2 row-start-1'
          : index === 5 
          ? 'col-start-3 row-start-2'
          : index === 6 
          ? 'col-start-4 row-start-1'
          : index === 7 
          ? 'col-start-4 row-start-2'
          : index === 8 
          ? 'col-start-4 row-start-3'
          : 'col-start-4 row-start-4';
      default:
        return '';
    }
  };

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
        <Button onClick={generateSpread}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-sun" />
          <p className="mt-4 text-ink/70 dark:text-parchment/60">Drawing your cards...</p>
        </div>
      ) : cards.length > 0 ? (
        <div className="space-y-8">
          <div className={`grid ${spreadLayouts[spreadType]} gap-8 p-4`}>
            {cards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className={`${getCardPosition(index)} relative ${spreadType === 'virtual-daily-card' ? 'w-32 mx-auto' : ''}`}
              >
                <button
                  onClick={() => setSelectedCard(card)}
                  className="w-full group"
                >
                  <div 
                    className={`aspect-[2/3] bg-vellum dark:bg-shadow rounded-lg shadow-lg overflow-hidden transform transition-transform hover:scale-105 ${
                      card.isReversed ? 'rotate-180' : ''
                    }`}
                  >
                    {card.image_url ? (
                      <img
                        src={card.image_url}
                        alt={card.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-4 text-center">
                        <span className="text-sm font-medium text-ink/70 dark:text-parchment/60">
                          {card.name}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 text-center space-y-2">
                    <p className="text-xl font-display font-bold text-ink dark:text-parchment">
                      {card.name}
                    </p>
                    <p className="text-sm text-sun dark:text-ember">
                      {card.arcana_type === 'major' ? 'Major Arcana' : `Minor Arcana • ${card.suit}`}
                      {card.isReversed && ' • Reversed'}
                    </p>
                    <p className="text-sm font-medium text-sun dark:text-ember">
                      {card.position}
                    </p>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>

          {/* Card Details Modal */}
          {selectedCard && (
            <div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedCard(null)}
            >
              <div 
                className="relative max-w-2xl w-full bg-vellum dark:bg-shadow rounded-lg shadow-xl overflow-y-auto max-h-[90vh]"
                onClick={e => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedCard(null)}
                  className="absolute top-4 right-4 text-ink/50 hover:text-ink dark:text-parchment/60 dark:hover:text-parchment"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Card Image */}
                    <div className="md:w-1/3">
                      {selectedCard.image_url && (
                        <img
                          src={selectedCard.image_url}
                          alt={selectedCard.name}
                          className={`w-full rounded-lg shadow-lg ${selectedCard.isReversed ? 'rotate-180' : ''}`}
                        />
                      )}
                      <div className="mt-4 text-center">
                        <h2 className="text-2xl font-display font-bold text-ink dark:text-parchment">
                          {selectedCard.name}
                        </h2>
                        <p className="text-sm text-sun dark:text-ember mt-1">
                          {selectedCard.arcana_type === 'major' ? 'Major Arcana' : `Minor Arcana • ${selectedCard.suit}`}
                          {selectedCard.isReversed ? ' • Reversed' : ''}
                        </p>
                        <p className="text-sm font-medium text-sun dark:text-ember mt-1">
                          {selectedCard.position}
                        </p>
                      </div>
                    </div>

                    {/* Card Details */}
                    <div className="md:w-2/3 space-y-6">
                      {selectedCard.meaning_summary && (
                        <div>
                          <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                            Card Meaning
                          </h3>
                          <div className="prose prose-gray dark:prose-invert max-w-none">
                            {selectedCard.meaning_summary.split('\n\n').map((paragraph, index) => (
                              <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                                {paragraph}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedCard.arcana_info && (
                        <div>
                          <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                            Arcana Information
                          </h3>
                          <div className="prose prose-gray dark:prose-invert max-w-none">
                            {selectedCard.arcana_info.split('\n\n').map((paragraph, index) => (
                              <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                                {paragraph}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedCard.themes && selectedCard.themes.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                            {selectedCard.isReversed ? 'Shadow Aspects' : 'Themes'}
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedCard.themes.map((theme: string, index: number) => (
                              <span
                                key={index}
                                className={`px-3 py-1 ${
                                  selectedCard.isReversed
                                    ? 'bg-vellum dark:bg-shadow/30 text-shadow dark:text-ember'
                                    : 'bg-vellum dark:bg-shadow/30 text-sun dark:text-ember'
                                } rounded-full text-sm`}
                              >
                                {theme.replace(/[{}]/g, '')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {!selectedCard.isReversed && selectedCard.shadow_themes && selectedCard.shadow_themes.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                            Shadow Aspects
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedCard.shadow_themes.map((theme: string, index: number) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-shadow dark:text-ember rounded-full text-sm"
                              >
                                {theme.replace(/[{}]/g, '')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedCard.traditional_imagery && (
                        <div>
                          <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                            Traditional Imagery
                          </h3>
                          <div className="prose prose-gray dark:prose-invert max-w-none">
                            {selectedCard.traditional_imagery.split('\n\n').map((paragraph, index) => (
                              <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                                {paragraph}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default VirtualSpread;